import type { CampoPropostoPelaIa, ParecerDaIa } from '../../../../types/chamado'
import type { FormularioDeMarcacao } from './esquema'

/**
 * A proposta da IA traduzida para os campos do formulário — e só aqui.
 *
 * **Por que a tradução mora num arquivo só.** O vocabulário da IA (`PROPOE_CORRETIVA`) é
 * deliberadamente diferente do valor que a marcação guarda (`CORRETIVA`), para que copiar um no
 * outro exija escrever a tradução à mão. Escrita uma vez, aqui, ela fica visível em revisão; o
 * servidor não tem tradução nenhuma — ele grava o que o formulário mandou e registra apenas os
 * NOMES dos campos que o fiscal não mexeu (migration V20).
 *
 * **Sobre pré-preencher "conforme, no prazo, aceito".** O formulário nasceu recusando valores
 * iniciais, e a razão continua válida: um padrão inventado pelo sistema transforma não responder em
 * aprovar. Uma proposta da IA é outra coisa — tem autor, justificativa e confiança, todas visíveis
 * ao lado do campo, e o registro guarda quais respostas o fiscal manteve como vieram. O que a torna
 * aceitável é isso; se a etiqueta ou a justificativa sumirem da tela, o pré-preenchimento deixa de
 * ser aceitável junto.
 *
 * Conformidade (ICA) e aceite (IAE) só ganham proposta quando o recorte leva as **interações** do
 * chamado — sem o relato do atendimento o modelo devolve `SEM_PROPOSTA`, e o fiscal responde
 * sozinho, que é o comportamento correto. "O chamado foi fechado" nunca é aceite: fechar é ato do
 * atendente, aceitar é ato do usuário.
 */

/** Os campos que a IA sabe propor. O banco repete esta lista num CHECK (V20). */
export const CAMPOS_PROPONIVEIS = ['natureza', 'prazo', 'criticidade', 'conformidade', 'aceite'] as const

export type CampoProponivel = (typeof CAMPOS_PROPONIVEIS)[number]

/** `PROPOE_X` → o valor que o campo do formulário usa. Fora da tabela, não há tradução. */
const TRADUCAO: Record<string, string> = {
  PROPOE_CORRETIVA: 'CORRETIVA',
  PROPOE_NAO_CORRETIVA: 'NAO_CORRETIVA',
  PROPOE_NO_PRAZO: 'NO_PRAZO',
  PROPOE_FORA_DO_PRAZO: 'FORA_DO_PRAZO',
  PROPOE_ALTA: 'ALTA',
  PROPOE_MEDIA: 'MEDIA',
  PROPOE_BAIXA: 'BAIXA',
  PROPOE_CONFORME: 'CONFORME',
  PROPOE_INCONFORME: 'INCONFORME',
  PROPOE_INTEGRAL: 'INTEGRAL',
  PROPOE_PARCIAL: 'PARCIAL',
  PROPOE_SEM_ACEITE: 'SEM_ACEITE',
}

/** O campo do formulário que cada nome de proposta preenche. */
const CAMPO_DO_FORMULARIO: Record<CampoProponivel, keyof FormularioDeMarcacao> = {
  natureza: 'natureza',
  prazo: 'prazo',
  criticidade: 'criticidadeTis',
  conformidade: 'conformidade',
  aceite: 'aceite',
}

/**
 * A proposta de um campo, quando o parecer traz uma que dê para usar.
 *
 * @param parecer o parecer exibido, quando há
 * @param campo qual campo
 * @returns a proposta com o valor já traduzido, ou nulo
 */
export function propostaDe(
  parecer: ParecerDaIa | null,
  campo: CampoProponivel,
): (CampoPropostoPelaIa & { valorNoFormulario: string }) | null {
  const proposta = parecer?.julgamentoProposto?.[campo]
  if (!proposta) {
    return null
  }
  const traduzido = TRADUCAO[proposta.valor]
  if (!traduzido) {
    // `SEM_PROPOSTA` cai aqui, e é o caso mais comum: a justificativa continua valendo para o
    // fiscal ("não dá para propor porque..."), mas não há valor para sugerir.
    return null
  }
  return { ...proposta, valorNoFormulario: traduzido }
}

/**
 * A justificativa da IA para um campo, mesmo quando ela não propôs valor.
 *
 * @param parecer o parecer exibido, quando há
 * @param campo qual campo
 * @returns o que a IA disse sobre este campo, ou nulo
 */
export function comentarioDe(parecer: ParecerDaIa | null, campo: CampoProponivel): CampoPropostoPelaIa | null {
  return parecer?.julgamentoProposto?.[campo] ?? null
}

/**
 * Os valores iniciais do formulário com as propostas aplicadas por cima.
 *
 * Só preenche campo ainda vazio: reedição de julgamento já registrado nunca é sobrescrita por
 * proposta — o que o fiscal decidiu antes vale mais que o que a máquina sugere agora.
 *
 * @param base os valores vindos da marcação gravada
 * @param parecer o parecer exibido, quando há
 * @returns os valores iniciais do formulário
 */
export function comPropostas(
  base: Partial<FormularioDeMarcacao>,
  parecer: ParecerDaIa | null,
): Partial<FormularioDeMarcacao> {
  if (!parecer) {
    return base
  }
  const valores: Record<string, unknown> = { ...base }
  for (const campo of CAMPOS_PROPONIVEIS) {
    const proposta = propostaDe(parecer, campo)
    const alvo = CAMPO_DO_FORMULARIO[campo]
    const atual = valores[alvo]
    if (proposta && (atual === undefined || atual === null || atual === '')) {
      valores[alvo] = proposta.valorNoFormulario
    }
  }
  return valores as Partial<FormularioDeMarcacao>
}

/**
 * Quais campos o fiscal está salvando exatamente como a IA propôs.
 *
 * É a trilha da V20 — nomes, nunca valores. Serve para responder, meses depois, se o julgamento
 * foi feito ou apenas assinado.
 *
 * @param valores o formulário como está sendo enviado
 * @param parecer o parecer exibido, quando há
 * @returns os nomes dos campos mantidos como propostos
 */
export function camposAceitos(valores: FormularioDeMarcacao, parecer: ParecerDaIa | null): string[] {
  if (!parecer) {
    return []
  }
  return CAMPOS_PROPONIVEIS.filter((campo) => {
    const proposta = propostaDe(parecer, campo)
    return proposta != null && valores[CAMPO_DO_FORMULARIO[campo]] === proposta.valorNoFormulario
  })
}
